import type { ChannelFile } from '../types';

export const TEXT_PREVIEW_BYTE_LIMIT = 1024 * 1024;
export const DOCX_PREVIEW_BYTE_LIMIT = 10 * 1024 * 1024;

export type FilePreviewKind =
  | 'image'
  | 'markdown'
  | 'code'
  | 'text'
  | 'pdf'
  | 'docx'
  | 'unsupported';

const CODE_EXTENSIONS = new Set([
  '.js',
  '.jsx',
  '.ts',
  '.tsx',
  '.py',
  '.css',
  '.json',
  '.sh',
  '.bash',
  '.html',
  '.xml',
  '.sql',
]);

const TEXT_EXTENSIONS = new Set(['.txt', '.text', '.log']);
const MARKDOWN_EXTENSIONS = new Set(['.md', '.markdown', '.mdown']);
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot).toLowerCase();
}

export function isCodeFile(name: string): boolean {
  return CODE_EXTENSIONS.has(fileExtension(name));
}

export function classifyPreviewFile(
  file: Pick<ChannelFile, 'name' | 'mime_type'>,
): FilePreviewKind {
  const extension = fileExtension(file.name);
  const mime = file.mime_type?.split(';', 1)[0].trim().toLowerCase() ?? '';

  if (extension === '.pdf' || mime === 'application/pdf') return 'pdf';
  if (extension === '.docx' || mime === DOCX_MIME) return 'docx';
  if (MARKDOWN_EXTENSIONS.has(extension)) return 'markdown';
  if (TEXT_EXTENSIONS.has(extension)) return 'text';
  if (isCodeFile(file.name)) return 'code';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('text/') || mime === 'application/json' || mime === 'application/xml') {
    return 'text';
  }
  return 'unsupported';
}

export class InvalidTextEncodingError extends Error {
  constructor() {
    super('The file is not valid UTF-8 text');
    this.name = 'InvalidTextEncodingError';
  }
}

export function decodeTextPreview(bytes: Uint8Array, truncated: boolean): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes, { stream: truncated });
  } catch {
    throw new InvalidTextEncodingError();
  }
}
